import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Rate, Button, Typography, Space, message, Spin, Result } from 'antd';
import { LeftOutlined, RightOutlined, CheckOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;

const GameRating = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tokenData, setTokenData] = useState(null);
  const [players, setPlayers] = useState([]);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [ratings, setRatings] = useState({});
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    validateTokenAndLoadData();
  }, [token]);

  const validateTokenAndLoadData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/game-ratings/validate/${token}`);
      const { token_data, players: gamePlayersData, existing_ratings } = response.data;
      
      setTokenData(token_data);
      setPlayers(gamePlayersData);
      setRatings(existing_ratings);
      
      // Encontrar primeiro jogador não avaliado
      const firstUnrated = gamePlayersData.findIndex(player => !existing_ratings[player.id]);
      setCurrentPlayerIndex(firstUnrated >= 0 ? firstUnrated : 0);
      
      setLoading(false);
    } catch (error) {
      message.error('Token inválido ou expirado');
      setLoading(false);
    }
  };

  const handleRating = async (value) => {
    const currentPlayer = players[currentPlayerIndex];
    
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/game-ratings/rate/${token}`, {
        player_id: currentPlayer.id,
        rating: value
      });

      // Atualizar ratings localmente
      setRatings(prev => ({
        ...prev,
        [currentPlayer.id]: value
      }));

      // Mover para próximo jogador automaticamente
      if (currentPlayerIndex < players.length - 1) {
        setCurrentPlayerIndex(currentPlayerIndex + 1);
      } else {
        // Finalizar avaliação
        await completeRating();
      }
    } catch (error) {
      message.error('Erro ao salvar avaliação');
    }
  };

  const completeRating = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/game-ratings/complete/${token}`);
      setCompleted(true);
    } catch (error) {
      message.error('Erro ao finalizar avaliação');
    }
  };

  const goToPrevious = () => {
    if (currentPlayerIndex > 0) {
      setCurrentPlayerIndex(currentPlayerIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentPlayerIndex < players.length - 1) {
      setCurrentPlayerIndex(currentPlayerIndex + 1);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!tokenData) {
    return (
      <Result
        status="error"
        title="Token Inválido"
        subTitle="O link de avaliação é inválido ou expirou."
      />
    );
  }

  if (completed) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: 20 }}>
        <Result
          status="success"
          title="Avaliação Concluída!"
          subTitle="Obrigado por avaliar os jogadores."
        />
        
        <Card style={{ marginTop: 20, textAlign: 'center' }}>
          <Title level={3}>Faça Parte da Comunidade Futezap!</Title>
          <Paragraph>
            Junte-se à nossa comunidade e aproveite todos os benefícios:
          </Paragraph>
          <ul style={{ textAlign: 'left', maxWidth: 400, margin: '0 auto' }}>
            <li>Envie convites para participar de jogos de outros times</li>
            <li>Ganhe pontos a cada participação</li>
            <li>Troque pontos por produtos e descontos</li>
            <li>Acesse a plataforma de recompensas Futezap</li>
            <li>Conecte-se com outros jogadores da região</li>
          </ul>
          
          <Space style={{ marginTop: 20 }}>
            <Button type="primary" size="large" onClick={() => navigate('/community/player/register')}>
              Cadastre-se
            </Button>
            <Button size="large" onClick={() => navigate('/community/player/login')}>
              Já tenho cadastro
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  const currentPlayer = players[currentPlayerIndex];
  const progress = ((currentPlayerIndex + 1) / players.length) * 100;

  return (
    <div style={{ maxWidth: 500, margin: '0 auto', padding: 20 }}>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <Title level={2}>Avalie os Jogadores</Title>
        <Text type="secondary">
          {tokenData.team_name} - {new Date(tokenData.date_of_game).toLocaleDateString('pt-BR')}
        </Text>
        <div style={{ margin: '10px 0' }}>
          <Text>{currentPlayerIndex + 1} de {players.length}</Text>
        </div>
        <div style={{ 
          width: '100%', 
          height: 4, 
          backgroundColor: '#f0f0f0', 
          borderRadius: 2,
          marginTop: 10
        }}>
          <div style={{ 
            width: `${progress}%`, 
            height: '100%', 
            backgroundColor: '#1890ff', 
            borderRadius: 2,
            transition: 'width 0.3s'
          }} />
        </div>
      </div>

      <Card style={{ textAlign: 'center', minHeight: 300 }}>
        <div style={{ marginBottom: 30 }}>
          <Title level={3}>{currentPlayer?.name}</Title>
          <Text type="secondary">{currentPlayer?.cellphone_number}</Text>
        </div>

        <div style={{ marginBottom: 30 }}>
          <Text>Como você avalia este jogador?</Text>
          <div style={{ marginTop: 15 }}>
            <Rate
              size="large"
              value={ratings[currentPlayer?.id] || 0}
              onChange={handleRating}
              style={{ fontSize: 40 }}
            />
          </div>
        </div>

        <Space>
          <Button
            icon={<LeftOutlined />}
            onClick={goToPrevious}
            disabled={currentPlayerIndex === 0}
          >
            Anterior
          </Button>
          
          {currentPlayerIndex < players.length - 1 ? (
            <Button
              icon={<RightOutlined />}
              onClick={goToNext}
              type="primary"
              disabled={!ratings[currentPlayer?.id]}
            >
              Próximo
            </Button>
          ) : (
            <Button
              icon={<CheckOutlined />}
              onClick={completeRating}
              type="primary"
              disabled={!ratings[currentPlayer?.id]}
            >
              Finalizar
            </Button>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default GameRating;